import { getUserLocale } from '@/i18n/LanguageUtilsService';

export type NumericValue = number | string | bigint;

export const isPersianLocale = () =>
  getUserLocale().toLowerCase().split('-')[0] === 'fa';

const parseNumericValue = (value: NumericValue): number | bigint | null => {
  if (typeof value === 'number' || typeof value === 'bigint') return value;
  if (value.trim() === '') return null;

  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Locale-aware formatting for values that are known to be numeric UI data.
 * This helper must not be used for IDs, URLs, usernames, or other technical
 * strings which merely happen to contain digits.
 */
export const formatNumber = (
  value: NumericValue,
  options?: Intl.NumberFormatOptions,
): string => {
  if (value === null || value === undefined) return '';

  const numericValue = parseNumericValue(value);
  if (numericValue === null) return String(value);

  return new Intl.NumberFormat(getUserLocale(), options).format(numericValue);
};

/**
 * Converts digits only in an explicitly user-facing display string.
 * Unlike `formatNumber`, this preserves existing precision and separators.
 */
export const formatLocalizedDigits = (value: string | number): string => {
  const displayValue = String(value);
  if (!isPersianLocale()) return displayValue;

  return displayValue.replace(/\d/g, (digit) =>
    String.fromCharCode(0x06f0 + Number(digit)),
  );
};

/**
 * Format a numeric value with locale-aware thousands separators.
 * When `compact` is true, uses compact notation (e.g. 1.2M, 1K)
 * suitable for dense UI like dashboard cards.
 */
export const formatUsageValue = (
  value: number | string,
  compact?: boolean,
): string => {
  if (value === null || value === undefined) return '';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return String(value);

  if (compact) {
    return formatNumber(num, {
      notation: 'compact',
      maximumFractionDigits: 1,
    });
  }

  return formatNumber(num);
};

/**
 * Converts Persian (۰-۹) and Arabic-Indic (٠-٩) digits to standard ASCII digits (0-9).
 */
export const toAsciiDigits = (str: string): string =>
  (str || '')
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660));

/**
 * Formats a numeric input string or number with standard comma thousands separators
 * while preserving decimal portions, minus signs, and typing state.
 */
export const formatWithSeparators = (val: string | number): string => {
  if (val === null || val === undefined || val === '') return '';
  let clean = toAsciiDigits(String(val)).replace(/,/g, '').trim();
  if (clean.includes('.') && clean.match(/\.0{2,}$/)) {
    clean = String(Number(clean));
  }
  if (clean === '' || isNaN(Number(clean))) {
    if (clean === '-' || clean === '.') return clean;
    return '';
  }
  const parts = clean.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
};

/**
 * Calculates the new cursor position after commas are dynamically inserted/removed,
 * by ensuring the cursor stays after the same count of input digits.
 */
export const getCursorPosAfterDigits = (
  formatted: string,
  digitsBefore: number,
  afterDot = false,
): number => {
  if (digitsBefore <= 0) {
    if (afterDot && formatted.includes('.')) {
      return formatted.indexOf('.') + 1;
    }
    return 0;
  }
  let count = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (/[0-9]/.test(formatted[i])) {
      count++;
      if (count === digitsBefore) {
        if (afterDot && formatted[i + 1] === '.') {
          return i + 2;
        }
        if (i + 1 < formatted.length && formatted[i + 1] === ',') {
          return i + 2;
        }
        return i + 1;
      }
    }
  }
  return formatted.length;
};
